import React, { useState, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import {
  Trophy, Upload, Camera, Plus, X, Loader2, Flag, Users, ChevronRight,
  Check, Target, DollarSign, Award, FileText, Trash2, Edit3, ChevronDown
} from 'lucide-react';

function ScoreCell({ value, par }) {
  if (!value || value === 0) return <span className="text-gray-500">-</span>;
  const diff = par ? value - par : 0;
  const color = diff < -1 ? 'text-yellow-400 font-black' : diff < 0 ? 'text-emerald-400 font-bold' : diff === 0 ? 'text-white' : diff === 1 ? 'text-red-400' : 'text-red-500 font-bold';
  return <span className={color}>{value}</span>;
}

export default function TournamentProcessor() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);

  // Check for incoming scan data
  React.useEffect(() => {
    const scanData = location.state?.scanData;
    if (scanData) {
      if (scanData.course_name) setCourseName(scanData.course_name);
      if (scanData.date) setTournDate(scanData.date);
      if (scanData.players?.length > 0) {
        setGroups([{
          label: 'Group 1',
          tee_time: '',
          players: scanData.players.map(p => ({
            name: p.name,
            scores: p.scores || {},
            front_9: p.front_9 || 0,
            back_9: p.back_9 || 0,
            total: p.total || 0,
            flagged_holes: p.flagged_holes || [],
          }))
        }]);
        setStep(2); // Go straight to review
      }
      // Clear the state so refresh doesn't re-import
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Setup
  const [step, setStep] = useState(0); // 0=setup, 1=upload, 2=review, 3=results
  const [courseName, setCourseName] = useState('');
  const [tournDate, setTournDate] = useState(new Date().toISOString().split('T')[0]);
  const [skinsPot, setSkinsPot] = useState('');

  // Groups & scanning
  const [groups, setGroups] = useState([{ label: 'Group 1', tee_time: '8:00 AM', players: [] }]);
  const [scanning, setScanning] = useState(false);
  const [scanningGroup, setScanningGroup] = useState(null);
  const [scanErrors, setScanErrors] = useState([]);

  // Results
  const [results, setResults] = useState(null);
  const [processing, setProcessing] = useState(false);

  // ============ GROUP MANAGEMENT ============

  const addGroup = () => {
    setGroups(prev => [...prev, { label: `Group ${prev.length + 1}`, tee_time: '', players: [] }]);
  };

  const removeGroup = (idx) => {
    setGroups(prev => prev.filter((_, i) => i !== idx));
  };

  const updateGroup = (idx, field, value) => {
    setGroups(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  // ============ SCORECARD SCANNING ============

  const scanScorecard = async (groupIdx, file) => {
    setScanning(true);
    setScanningGroup(groupIdx);
    setScanErrors(prev => prev.filter(e => e.group !== groupIdx));

    const formData = new FormData();
    formData.append('file', file);
    formData.append('group_label', groups[groupIdx].label);

    try {
      const res = await fetch(`${API}/tournament-processor/scan-scorecard`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) throw new Error('Scan failed');
      const { data } = await res.json();

      if (data.error) {
        setScanErrors(prev => [...prev, { group: groupIdx, error: data.error }]);
      } else {
        const players = (data.players || []).map(p => ({
          name: p.name || 'Unknown',
          scores: p.scores || {},
          front_9: p.front_9 || 0,
          back_9: p.back_9 || 0,
          total: p.total || 0,
          flagged_holes: p.flagged_holes || [],
        }));
        if (data.course_name && !courseName) setCourseName(data.course_name);
        setGroups(prev => prev.map((g, i) => i === groupIdx ? { ...g, players: [...g.players, ...players] } : g));
      }
    } catch (err) {
      setScanErrors(prev => [...prev, { group: groupIdx, error: err.message }]);
    } finally {
      setScanning(false);
      setScanningGroup(null);
    }
  };

  // ============ MANUAL PLAYER ADD ============

  const addManualPlayer = (groupIdx) => {
    const name = prompt('Player name:');
    if (!name) return;
    const scores = {};
    for (let h = 1; h <= 18; h++) scores[String(h)] = 0;
    setGroups(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, players: [...g.players, { name, scores, front_9: 0, back_9: 0, total: 0, flagged_holes: [] }] } : g
    ));
  };

  const removePlayer = (groupIdx, playerIdx) => {
    setGroups(prev => prev.map((g, i) =>
      i === groupIdx ? { ...g, players: g.players.filter((_, j) => j !== playerIdx) } : g
    ));
  };

  const updateScore = (groupIdx, playerIdx, hole, value) => {
    const val = parseInt(value) || 0;
    setGroups(prev => prev.map((g, gi) => {
      if (gi !== groupIdx) return g;
      const players = g.players.map((p, pi) => {
        if (pi !== playerIdx) return p;
        const scores = { ...p.scores, [hole]: val };
        const front_9 = Object.entries(scores).filter(([h]) => parseInt(h) <= 9).reduce((a, [, s]) => a + (s || 0), 0);
        const back_9 = Object.entries(scores).filter(([h]) => parseInt(h) > 9).reduce((a, [, s]) => a + (s || 0), 0);
        return { ...p, scores, front_9, back_9, total: front_9 + back_9 };
      });
      return { ...g, players };
    }));
  };

  // ============ PROCESS TOURNAMENT ============

  const processTournament = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/tournament-processor/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          course_name: courseName || 'Unknown Course',
          date: tournDate,
          groups,
          skins_pot: parseFloat(skinsPot) || 0,
        }),
      });
      if (!res.ok) throw new Error('Processing failed');
      const data = await res.json();
      setResults(data);
      setStep(3);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const totalPlayers = groups.reduce((a, g) => a + g.players.length, 0);

  // ============ STEP 0: SETUP ============
  if (step === 0) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-black" data-testid="processor-title">Tournament Processor</h1>
              <p className="text-sm text-gray-400">Scan scorecards, calculate results</p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Course Name *</label>
              <input data-testid="course-name-input" value={courseName} onChange={e => setCourseName(e.target.value)} placeholder="e.g. National Golf Club" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Date</label>
                <input type="date" value={tournDate} onChange={e => setTournDate(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-500/50" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Skins Pot ($)</label>
                <input type="number" value={skinsPot} onChange={e => setSkinsPot(e.target.value)} placeholder="0" className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50" />
              </div>
            </div>

            {/* Groups */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-gray-400">Tee Time Groups</label>
                <button onClick={addGroup} className="text-xs text-emerald-400 hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Group
                </button>
              </div>
              <div className="space-y-3">
                {groups.map((g, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={g.label} onChange={e => updateGroup(i, 'label', e.target.value)} placeholder="Group name" className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                    <input value={g.tee_time} onChange={e => updateGroup(i, 'tee_time', e.target.value)} placeholder="9:00 AM" className="w-28 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                    {groups.length > 1 && (
                      <button onClick={() => removeGroup(i)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <Button data-testid="next-to-upload" onClick={() => setStep(1)} disabled={!courseName} className="w-full mt-8 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-5 rounded-xl">
            Next: Upload Scorecards <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ============ STEP 1: UPLOAD ============
  if (step === 1) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-lg mx-auto px-4 py-6">
          <button onClick={() => setStep(0)} className="flex items-center gap-1 text-gray-400 hover:text-white mb-4 text-sm">
            <ChevronDown className="w-4 h-4 rotate-90" /> Back to Setup
          </button>
          <h2 className="text-xl font-black mb-2">Upload Scorecards</h2>
          <p className="text-sm text-gray-400 mb-6">Scan one scorecard image per group. AI will extract all player scores.</p>

          <div className="space-y-4">
            {groups.map((g, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold">{g.label}</h3>
                    {g.tee_time && <p className="text-xs text-gray-500">{g.tee_time}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {g.players.length > 0 && (
                      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                        {g.players.length} players
                      </span>
                    )}
                  </div>
                </div>

                {g.players.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {g.players.map((p, j) => (
                      <div key={j} className="flex items-center justify-between text-sm px-3 py-1.5 bg-white/5 rounded-lg">
                        <span>{p.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{p.total || '--'}</span>
                          <button onClick={() => removePlayer(i, j)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={el => { if (i === 0) fileRef.current = el; }}
                    id={`file-${i}`}
                    onChange={e => { if (e.target.files[0]) scanScorecard(i, e.target.files[0]); }}
                  />
                  <Button
                    onClick={() => document.getElementById(`file-${i}`).click()}
                    disabled={scanning}
                    className="flex-1 bg-white/10 hover:bg-white/15 text-gray-300 font-medium"
                  >
                    {scanning && scanningGroup === i ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning...</>
                    ) : (
                      <><Camera className="w-4 h-4 mr-2" /> Scan Scorecard</>
                    )}
                  </Button>
                  <Button onClick={() => addManualPlayer(i)} variant="outline" className="border-white/10 text-gray-400">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {scanErrors.filter(e => e.group === i).map((e, j) => (
                  <p key={j} className="text-xs text-red-400 mt-2">{e.error}</p>
                ))}
              </div>
            ))}
          </div>

          <Button
            data-testid="next-to-review"
            onClick={() => setStep(2)}
            disabled={totalPlayers === 0}
            className="w-full mt-6 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-5 rounded-xl"
          >
            Review Scores ({totalPlayers} players) <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ============ STEP 2: REVIEW & EDIT ============
  if (step === 2) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button onClick={() => setStep(1)} className="flex items-center gap-1 text-gray-400 hover:text-white mb-4 text-sm">
            <ChevronDown className="w-4 h-4 rotate-90" /> Back to Upload
          </button>
          <h2 className="text-xl font-black mb-2">Review & Edit Scores</h2>
          <p className="text-sm text-gray-400 mb-6">Verify all scores. Edit any that look incorrect.</p>

          {groups.map((g, gi) => g.players.length > 0 && (
            <div key={gi} className="mb-6">
              <h3 className="font-bold text-emerald-400 mb-2">{g.label} {g.tee_time && `— ${g.tee_time}`}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-1 text-gray-500 sticky left-0 bg-[#0a1a0f]">Player</th>
                      {Array.from({ length: 9 }, (_, i) => (
                        <th key={i} className="text-center py-2 px-1 text-gray-500 w-8">{i + 1}</th>
                      ))}
                      <th className="text-center py-2 px-1 text-emerald-400 font-bold">OUT</th>
                      {Array.from({ length: 9 }, (_, i) => (
                        <th key={i + 9} className="text-center py-2 px-1 text-gray-500 w-8">{i + 10}</th>
                      ))}
                      <th className="text-center py-2 px-1 text-emerald-400 font-bold">IN</th>
                      <th className="text-center py-2 px-1 text-white font-bold">TOT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.players.map((p, pi) => (
                      <tr key={pi} className="border-b border-white/5">
                        <td className="py-2 px-1 font-medium text-white sticky left-0 bg-[#0a1a0f] max-w-[80px] truncate">{p.name}</td>
                        {Array.from({ length: 9 }, (_, h) => (
                          <td key={h} className="text-center py-1 px-0.5">
                            <input
                              type="number"
                              min="0"
                              max="15"
                              value={p.scores[String(h + 1)] || ''}
                              onChange={e => updateScore(gi, pi, String(h + 1), e.target.value)}
                              className={`w-7 h-7 text-center rounded bg-white/5 border text-xs ${
                                p.flagged_holes?.includes(h + 1) ? 'border-yellow-500/50 text-yellow-400' : 'border-white/10 text-white'
                              } focus:outline-none focus:border-emerald-500`}
                            />
                          </td>
                        ))}
                        <td className="text-center py-2 px-1 font-bold text-emerald-400">{p.front_9 || '--'}</td>
                        {Array.from({ length: 9 }, (_, h) => (
                          <td key={h + 9} className="text-center py-1 px-0.5">
                            <input
                              type="number"
                              min="0"
                              max="15"
                              value={p.scores[String(h + 10)] || ''}
                              onChange={e => updateScore(gi, pi, String(h + 10), e.target.value)}
                              className={`w-7 h-7 text-center rounded bg-white/5 border text-xs ${
                                p.flagged_holes?.includes(h + 10) ? 'border-yellow-500/50 text-yellow-400' : 'border-white/10 text-white'
                              } focus:outline-none focus:border-emerald-500`}
                            />
                          </td>
                        ))}
                        <td className="text-center py-2 px-1 font-bold text-emerald-400">{p.back_9 || '--'}</td>
                        <td className="text-center py-2 px-1 font-black text-white text-sm">{p.total || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <Button
            data-testid="process-tournament-btn"
            onClick={processTournament}
            disabled={processing}
            className="w-full mt-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-5 rounded-xl"
          >
            {processing ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</> : <><Trophy className="w-5 h-5 mr-2" /> Generate Tournament Results</>}
          </Button>
        </div>
      </div>
    );
  }

  // ============ STEP 3: RESULTS ============
  if (step === 3 && results) {
    const { leaderboard, skins, skins_payout, insights, admin_script, groups: resGroups } = results;
    const skinHoles = skins?.holes || {};
    const skinTotals = skins?.player_totals || {};

    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-lg mx-auto px-4 py-6">

          {/* Header */}
          <div className="text-center mb-8">
            <Trophy className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-3xl font-black">Tournament Summary</h1>
            <p className="text-gray-400">{results.course_name}</p>
            <p className="text-gray-500 text-sm">{results.date}</p>
          </div>

          {/* LEADERBOARD */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
            <h2 className="text-lg font-black flex items-center gap-2 mb-4"><Award className="w-5 h-5 text-emerald-400" /> Leaderboard</h2>
            <div className="space-y-2">
              {leaderboard?.slice(0, 10).map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white/5">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      p.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                      p.rank === 2 ? 'bg-gray-300 text-gray-700' :
                      p.rank === 3 ? 'bg-orange-400 text-orange-900' :
                      'bg-white/10 text-gray-400'
                    }`}>{p.rank}</span>
                    <div>
                      <div className="font-bold">{p.name} {p.tied && <span className="text-xs text-yellow-400">(T)</span>}</div>
                      <div className="text-xs text-gray-500">{p.front_9} + {p.back_9} | {p.holes_played} holes</div>
                    </div>
                  </div>
                  <div className="text-2xl font-black">{p.total}</div>
                </div>
              ))}
            </div>
          </div>

          {/* SKINS */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
            <h2 className="text-lg font-black flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5 text-emerald-400" /> Skins Results</h2>
            <div className="grid grid-cols-3 gap-1 text-xs mb-4">
              {Object.entries(skinHoles).map(([hole, data]) => (
                <div key={hole} className={`px-2 py-1.5 rounded-lg text-center ${data ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/5'}`}>
                  <div className="text-gray-500">H{hole}</div>
                  <div className={data ? 'text-emerald-400 font-bold' : 'text-gray-600'}>
                    {data ? `${data.winner} (${data.score})` : 'Push'}
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(skinTotals).length > 0 && (
              <div className="border-t border-white/10 pt-3 space-y-2">
                <h3 className="text-sm text-gray-400 font-bold">Total Skins</h3>
                {Object.entries(skinTotals).map(([name, count]) => (
                  <div key={name} className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg">
                    <span className="font-medium">{name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-emerald-400 font-bold">{count} skin{count > 1 ? 's' : ''}</span>
                      {skins_payout?.[name] && <span className="text-yellow-400 font-bold">${skins_payout[name]}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* GROUP BREAKDOWN */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
            <h2 className="text-lg font-black flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-emerald-400" /> Group Breakdown</h2>
            {resGroups?.map((g, i) => (
              <div key={i} className="mb-3 last:mb-0">
                <h3 className="text-sm text-gray-400 mb-1">{g.label} {g.tee_time && `— ${g.tee_time}`}</h3>
                {g.players?.map((p, j) => (
                  <div key={j} className="flex justify-between px-3 py-1.5 text-sm">
                    <span>{p.name}</span>
                    <span className="font-bold">{p.total || '--'}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* INSIGHTS */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
            <h2 className="text-lg font-black flex items-center gap-2 mb-4"><Target className="w-5 h-5 text-emerald-400" /> Insights</h2>
            <ul className="space-y-2">
              {insights?.map((insight, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                  {insight}
                </li>
              ))}
            </ul>
          </div>

          {/* ADMIN SCRIPT */}
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5 mb-6">
            <h2 className="text-lg font-black flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-emerald-400" /> Admin Talking Points</h2>
            <p className="text-sm text-emerald-100 leading-relaxed italic">"{admin_script}"</p>
          </div>

          <div className="flex gap-3">
            <Button onClick={() => { setStep(0); setResults(null); }} variant="outline" className="flex-1 border-white/10 text-gray-400">
              New Tournament
            </Button>
            <Button onClick={() => navigate('/dashboard')} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
              Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
